/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import _isString from 'lodash/isString';

import {
  List,
  Datagrid,
  TextField,
  DateField,
  Filter,
  TextInput,
  EditButton,
  useRedirect,
} from 'react-admin';

import { useLocation } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import FilledInput from '@mui/material/FilledInput';

import Pagination from '../../components/Pagination';
import AssetThumbnail from '../../components/AssetThumbnail';
import StatusSelectorInput from '../../components/StatusSelectorInput';
import BulkPublishButton from '../../components/BulkPublishButton';
import BulkTagButton from '../../components/BulkTagButton';
import BulkArchiveButton from '../../components/BulkArchiveButton';
import AssetTypeSelectorInput from '../../components/AssetTypeSelectorInput';
import { isRefSearch } from '../../helpers/dataHelpers';

const defaultSort = { field: 'createdAt', order: 'DESC' };
const searchSort = { field: 'search', order: 'DESC' };

const AssetFilter = ({ onFilterChange, ...props }) => {
  const redirect = useRedirect();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const filterSearchParams = searchParams.get('filter');
  const filterURL = filterSearchParams ? JSON.parse(filterSearchParams) : {};
  const [filledInputVal, setFilledInputVal] = useState(filterURL.q);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleChangeFilter('q', event.target.value);
    }
  };

  const updateSortParams = () => {
    const query = filterURL['q'];
    if (_isString(query) && query !== '' && !isRefSearch(query)) {
      searchParams.set('sort', searchSort.field);
      searchParams.set('order', searchSort.order);
    } else {
      searchParams.set('sort', defaultSort.field);
      searchParams.set('order', defaultSort.order);
    }
  };

  const handleChangeFilter = (filter, value) => {
    onFilterChange();
    filterURL[filter] = value;
    searchParams.set('filter', JSON.stringify(filterURL));
    updateSortParams(filter, value);

    redirect(`/assets?${searchParams}`);
  };

  return (
    <Filter {...props}>
      <div style={{ marginBottom: '9px' }} alwaysOn>
        <Button
          variant="outlined"
          onClick={() => handleChangeFilter('status', 'draft')}
        >
          Drafts
        </Button>
      </div>

      <FilledInput
        value={filledInputVal}
        onChange={(event) => setFilledInputVal(event.target.value)}
        onKeyDown={handleKeyDown}
        hiddenLabel
        placeholder="Search"
        variant="filled"
        style={{ marginBottom: '8px' }}
        size="small"
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="search on asset list"
              onClick={() => {
                setFilledInputVal('');
                handleChangeFilter('q', '');
              }}
              disabled={!filledInputVal}
            >
              {!filledInputVal ? <SearchIcon /> : <CloseIcon />}
            </IconButton>
          </InputAdornment>
        }
        alwaysOn
      />

      <StatusSelectorInput label="Status" source="status" />
      <AssetTypeSelectorInput label="Asset type" source="typename" />
      <TextInput label="Id" source="ids" />
      <TextInput label="Upload Tag" source="extraData.uploadTag" resettable />
    </Filter>
  );
};

AssetFilter.propTypes = {
  onFilterChange: PropTypes.func,
};

AssetFilter.defaultProps = {
  onFilterChange: () => {},
};

const PostActions = (props) => (
  <Fragment>
    <BulkTagButton {...props} />
    <BulkPublishButton {...props} />
    <BulkArchiveButton {...props} />
  </Fragment>
);

function AssetsList({ hasEdit, location, ...rest }) {
  return (
    <List
      {...rest}
      title="Assets Listing"
      pagination={<Pagination />}
      filters={<AssetFilter />}
      sort={defaultSort}
      bulkActionButtons={hasEdit ? <PostActions /> : false}
      exporter={false}
    >
      <Datagrid>
        <AssetThumbnail
          source="thumbnail.url"
          label="Thumbnail"
          title="title"
          sortable={false}
        />
        <TextField source="title" />
        <TextField source="assetTypeLabel" label="Type" sortable={false} />
        <TextField source="extraData.ref" label="Reference" sortable={false} />
        <TextField source="status" sortable={false} />
        <TextField
          source="extraData.uploadTag"
          label="Upload Tag"
          sortable={false}
        />
        <DateField source="createdAt" showTime />
        <DateField source="updatedAt" showTime sortable={false} />
        {hasEdit && <EditButton />}
      </Datagrid>
    </List>
  );
}

export default AssetsList;
